import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useChannel } from '@/lib/realtime';
import { tenantApi } from '@/modules/tenant/services/tenantApi';
import { notificationKeys } from './useNotifications';

interface TokenResponse {
  token: string;
  channels: string[];
}

/**
 * #740 — subscribe to the caller's notifications channel and refresh the bell
 * on each pushed event. The channel string is read from the realtime token
 * response (server-authoritative; embeds tenant + user), so no tenant id is
 * needed client-side.
 */
export function useNotificationChannel(): void {
  const qc = useQueryClient();

  const { data: channel } = useQuery({
    queryKey: ['realtime', 'notifications-channel'],
    queryFn: async () => {
      const res = await tenantApi.get<TokenResponse>('/realtime/token');
      // MUST be user-scoped: the discovery array ALSO contains the tenant-wide
      // `tenant.{t}.notifications` broadcast channel, which also ends in
      // `.notifications`. Match the per-user channel specifically.
      return res.data.channels.find(
        (c) => c.startsWith('user.') && c.endsWith('.notifications'),
      ) ?? null;
    },
    staleTime: Infinity,
  });

  useChannel(channel ?? '', {
    onPublication: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
